import { theme } from 'antd';
import { UnassignCredentialIcon } from 'assets/images/svg';
import { useManagePermittedRoles } from 'library/hooks';
import { Fragment, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCredentialByID, selectCredentialLoading } from 'reduxStore/credentials/selectors';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { Button, Flex, Modal, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const UnassignCredentialsModal = ({
	visible: visibleProp,
	setVisible: setVisibleProp,
	children,
	credentialID,
	personID: personIDProp
}) => {
	// ✅ Hooks
	const {
		token: { paddingLG, fontSizeSM, paddingSM, marginXS, paddingXL, colorWarning, padding }
	} = theme.useToken();
	const dispatch = useDispatch();

	// ✅ Redux state
	const credential = useSelector((state) => selectCredentialByID(state, credentialID));
	const isCredentialLoading = !!useSelector((state) => selectCredentialLoading(state, credentialID));
	const personID = personIDProp || credential?.personID;

	// ✅ Permissions
	const canManageCredential = useManagePermittedRoles('updateCredential');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	// ✅ Local state
	const [internalVisible, setInternalVisible] = useState(false);
	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	const isAssigned = Boolean(personID);
	const isTriggerDisabled = isCredentialLoading || !canManageCredential || !isAssigned;

	const handleCancel = useCallback(() => setVisible(false), [setVisible]);

	const handleUnassign = () => {
		dispatch(
			credentialsActions.unassignCredential({ credentialID, data: { oldPersonID: personID } })
		);

		setTimeout(() => {
			handleCancel();
		}, 500);
	};

	return (
		<>
			<Tooltip
				title={
					!canManageCredential
						? permissionDeniedMessage
						: !isAssigned
							? 'Credential is not assigned.'
							: 'Unassign Credential'
				}
				placement='top'
			>
				<span onClick={(e) => canManageCredential && isAssigned && setVisible(true)}>
					{children ?? (
						<Button
							type='text'
							size='small'
							icon={<UnassignCredentialIcon color='currentColor' />}
							disabled={isTriggerDisabled || !canManageCredential}
						/>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Flex
						align='center'
						justify='center'
						style={{ marginTop: padding, padding: `0px ${marginXS}px` }}
						gap={paddingSM}
						key='footer'
					>
						<Button onClick={handleCancel} type='default' disabled={isCredentialLoading} block>
							Cancel
						</Button>
						<Button
							onClick={handleUnassign}
							loading={isCredentialLoading}
							disabled={isCredentialLoading}
							type='button'
							block
							style={{
								backgroundColor: colorWarning,
								borderColor: colorWarning,
								color: '#fff'
							}}
						>
							Unassign
						</Button>
					</Flex>
				]}
				width={350}
				styles={{ content: { padding: paddingXL } }}
			>
				<Flex vertical align='center' justify='center' gap={marginXS}>
					<UnassignCredentialIcon width={80} height={80} />
					<Flex vertical gap={marginXS}>
						<Title
							level={5}
							style={{ margin: 0, lineHeight: '24px', textAlign: 'center', padding: `0 ${paddingLG}px` }}
						>
							{'Are you sure you want to unassign this credential?'}
						</Title>
						<Text style={{ fontSize: fontSizeSM, lineHeight: '20px', textAlign: 'center' }}>
							This action completely removes all associations between the credential and the person,
							resetting the credential to a blank state with no access permissions.
						</Text>
					</Flex>
				</Flex>
			</Modal>
		</>
	);
};

export default UnassignCredentialsModal;
